import { RISK_PT } from '../../lib/concelhos';
import type { RiskLevel } from '../../types';

// Freguesia rows, hottest first. This is the operational view: the official index gives
// one class for the whole concelho, so "which of my freguesias carries the risk today"
// is the question it cannot answer and this can. A municipal corpo de bombeiros maps
// roughly one-to-one onto a freguesia, which is why the ordering is the product.

export interface FreguesiaRow {
  nome: string;
  dtmnfr?: string | null;
  n_cells: number;
  risk: Record<string, number>;
  mod_plus: number;
  mod_plus_pct: number;
  mean_prob: number;
  max_prob: number;
  anomaly_mean?: number | null;
}

/** Shorten CAOP's official "União das freguesias de X, Y e Z" for display only. */
export function shortFreguesia(nome: string): string {
  return nome.replace(/^Uni[aã]o das freguesias de\s+/i, '');
}

export default function FreguesiaTable({ rows }: { rows: FreguesiaRow[] }) {
  if (!rows.length) return null;
  const peak = Math.max(...rows.map((r) => r.mean_prob), 1e-9);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[34rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-ink-700 text-left font-mono text-xs uppercase tracking-widest text-mist-faint">
            <th className="py-2 pr-3 font-normal">Freguesia</th>
            <th className="py-2 pr-3 text-right font-normal">Células</th>
            <th className="py-2 pr-3 font-normal">Pontuação relativa</th>
            <th className="py-2 pr-3 text-right font-normal">Moderado+</th>
            <th className="py-2 text-right font-normal">Dominante</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const dominant = (Object.entries(r.risk).sort(
              (a, b) => b[1] - a[1],
            )[0]?.[0] ?? 'Low') as RiskLevel;
            return (
              <tr
                key={r.dtmnfr ?? r.nome}
                className={`border-b border-ink-800 ${i === 0 ? 'text-mist' : 'text-mist-muted'}`}
              >
                <td className="py-2.5 pr-3">
                  <span className={i === 0 ? 'font-semibold' : ''}>
                    {shortFreguesia(r.nome)}
                  </span>
                </td>
                <td className="py-2.5 pr-3 text-right font-mono text-xs">{r.n_cells}</td>
                <td className="py-2.5 pr-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-ink-800">
                      <div
                        className="h-full rounded-full bg-ember"
                        style={{ width: `${Math.max(2, (r.mean_prob / peak) * 100)}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs text-mist-faint">
                      {r.mean_prob.toFixed(4)}
                    </span>
                  </div>
                </td>
                <td className="py-2.5 pr-3 text-right font-mono text-xs">
                  {r.mod_plus_pct}%
                </td>
                <td className="py-2.5 text-right font-mono text-xs">{RISK_PT[dominant]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
